import type { IconName } from "@mdotm/shadcn/legacy/components";
import { atom, atomWithCompare, atomWithStorage, useAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import type { ReactNode } from "react";
import { useWindowSize } from "@mdotm/shadcn/legacy/react-extensions";
import type { Updater } from "@mdotm/shadcn/legacy/utils";
import type { DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import type { SidebarPositions } from "../common/Sidebar-shared";
import env from "$root/env";
import { UserRole, useUserValue } from "$root/functional-areas/user";

/**
 * React Query Devtools are a dev aid, surfaced from the user module. Available in every
 * non-production environment — `appEnv` has two classes, so "not production" is the whole of
 * "dev-ish" — and, in any environment, to ROOT users.
 */
export function useDevtoolsEnabled(): boolean {
	const user = useUserValue();
	return env.appEnv === "development" || user.roles.some((role) => role === UserRole.ROOT);
}

/** Whether the hoisted React Query Devtools panel is open. Lives here so it survives drawer-module unmounts. */
export const devtoolsPanelOpen$ = atom(false);
/**
 * Height (px) of the hoisted devtools panel. The bare `ReactQueryDevtoolsPanel` has no resize state
 * of its own (that lives in the full `ReactQueryDevtools` wrapper we can't use here), so we own it —
 * persisted so a drag survives closing/reopening the panel, mirroring the wrapper's behavior.
 */
export const devtoolsPanelHeight$ = atomWithStorage<number>("mdotm.devtoolsPanelHeight", 500);
export const devtoolsPanelMinHeight = 150;

export const collapsedDrawerWidth = 61;
export const floatingDrawerWidth = 235;
export const drawerWidth = collapsedDrawerWidth + floatingDrawerWidth;
const windowWidthThreshold = 1500;

export const mainSidebarPosition$ = atomWithStorage<SidebarPositions>("mainSidebarPositionAtom", "right");
export const mainActiveSidebar$ = atomWithCompare<"chat" | "notifications" | null>(null);
export const appDrawerUserExpand$ = atomWithStorage<boolean>("appDrawerUserExpandStoreAtom", true);

export function useAppDrawerState(): {
	setPin(pin: boolean | Updater<boolean>): void;
	isPinned: boolean;
	canPin: boolean;
	width: number;
} {
	const [pin, setPin] = useAtom(appDrawerUserExpand$);
	const { width: windowWidth } = useWindowSize();
	const canPin = windowWidth > windowWidthThreshold;
	const isPinned = canPin && pin;
	return {
		isPinned,
		canPin,
		setPin,
		width: isPinned ? drawerWidth : collapsedDrawerWidth,
	};
}

export function useAppDrawerWidth() {
	const pin = useAtomValue(appDrawerUserExpand$);
	const { width: windowWidth } = useWindowSize();
	const canPin = windowWidth > windowWidthThreshold;
	const isPinned = canPin && pin;
	return isPinned ? drawerWidth : collapsedDrawerWidth;
}

export type AppDrawerModule = {
	enabled: boolean;
	background?: string;
	active: boolean;
	icon: IconName | (string & {}) | Exclude<ReactNode, string>;
	name: string;
	title: string;
	children?: ReactNode;
} & (
	| {
			onClick(): void;
	  }
	| {
			href: string;
	  }
	| {
			onClickAsync(): Promise<void>;
	  }
) &
	DataAttributesProps;

export type AppDrawerModuleItem<Name extends string = string> = {
	icon: IconName | (string & {}) | Exclude<ReactNode, string>;
	enabled: boolean;
	active: boolean;
	name: Name;
	title: string;
	qualifier?: string;
	children?: AppDrawerModuleItem[];
} & (
	| {
			onClick(): void;
	  }
	| {
			href: string;
	  }
	| {
			onClickAsync(): Promise<void>;
	  }
);
